import React from "react";
import AILearningSummary from "../AILearningSummary";
import AssignAiTestFlow, { FLOW_STEPS } from "../../AssignAiTestFlow";
import { SvgIcons } from "../../../SharedComponents/SvgIcons/SvgIcons";
import Tooltip from "../../UI/Tooltip";
import useAIActions from "./useAIActions";
import styles from "./style.module.css";

const SUMMARY_DISABLED_TEXT = "No AI activities done by student since the last class";
const ASSIGN_TEST_DISABLED_TEXT = "Assign a test is available for Math and English only";

/**
 * The two AI actions — "Learning summary" (opens the AILearningSummary modal,
 * or the Learning Summary page when `opensSummaryPage` is set) and "Assign
 * test" (opens AssignAiTestFlow at the test-type step) — rendered as upfront
 * buttons rather than a dropdown, so neither is a click away. Self-contained
 * so it can be dropped into any card.
 */
// `subject` is the class courseSection and `studentIds` every student in the
// session — both drive the assign-test flow (grade levels + submit payload).
// `courseName` and `source` only label the tracking events — `source` is the
// surface the buttons are mounted on, so the dashboard card and the student
// page don't both report "dashboard".
const AIActionsDropdown = ({ student, studentName, studentIds, subject, batchId, showStudentBrief, className, courseName, source = "dashboard", opensSummaryPage = false, returnUrl }) => {
  const {
    summaryOpen,
    assignOpen,
    canAssignTest,
    handleViewLearningSummary,
    handleAssignTest,
    closeSummary,
    closeAssign,
  } = useAIActions({ student, studentName, subject, batchId, courseName, source, opensSummaryPage, returnUrl });

  // Both actions are upfront buttons now, so each is tracked where it opens.
  // `ai_actions_opened` is gone with the dropdown it measured — see the note by
  // the component's export.
  return (
    <div className={`${styles.wrapper} ${className || ""}`}>
      {/* aria-disabled instead of disabled — keeps the button hoverable so the
          tooltip explaining the greyed-out state still fires. With no menu left
          to open and inspect, that tooltip is the only affordance saying why. */}
      <Tooltip
        text={showStudentBrief ? "" : SUMMARY_DISABLED_TEXT}
        position="top"
        multiline
        customStyles={{ wrapper: styles.actionTooltip }}
      >
        <button
          className={`${styles.actionBtn} ${styles.summaryBtn} ${!showStudentBrief ? styles.actionBtnDisabled : ""}`}
          aria-disabled={!showStudentBrief}
          onClick={showStudentBrief ? handleViewLearningSummary : undefined}
        >
          <SvgIcons name="file_icon" width={14} height={14} style={{ flexShrink: 0 }} />
          <span className={styles.actionLabel}>Learning summary</span>
        </button>
      </Tooltip>

      <Tooltip
        text={canAssignTest ? "" : ASSIGN_TEST_DISABLED_TEXT}
        position="top"
        multiline
        customStyles={{ wrapper: styles.actionTooltip }}
      >
        <button
          className={`${styles.actionBtn} ${styles.assignTestBtn} ${!canAssignTest ? styles.actionBtnDisabled : ""}`}
          aria-disabled={!canAssignTest}
          onClick={canAssignTest ? handleAssignTest : undefined}
        >
          <SvgIcons name="edit_icon" width={14} height={14} style={{ flexShrink: 0 }} />
          <span className={styles.actionLabel}>Assign test</span>
        </button>
      </Tooltip>

      <AILearningSummary
        open={summaryOpen}
        onClose={closeSummary}
        student={student}
        batchId={batchId}
      />

      <AssignAiTestFlow
        open={assignOpen}
        student={student}
        studentIds={studentIds}
        subject={subject}
        initialStep={FLOW_STEPS?.TEST_TYPE}
        onClose={closeAssign}
      />
    </div>
  );
};

// Tracking note: the tracking sheet's `ai_actions_opened` ("When AI actions
// dropdown is opened") no longer has a trigger — develop replaced the dropdown
// with these two upfront buttons, so there is no open/close step left to
// measure. The two actions it used to gate are tracked directly instead.
export default AIActionsDropdown;
